import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import { NAV_CATALOG } from '../../config/navCatalog';
import { NAV_ICONS, ICON_MAP } from '../../config/navIcons';
import { T as DS } from '../../design-system/tokens';
import BarcodeScanner from '../BarcodeScanner';
import GlobalSearchResults from '../search/GlobalSearchResults';
import { T } from '../../design-system';
import api from '../../utils/api';

// Burgundy, not bronze: it is the app icon, the splash, and every primary
// action, so the active tab and the selected module now agree with the rest of
// the identity instead of introducing a second brand colour.
const ACCENT = T.accent;
const SHELL   = T.accent;
const MUTED   = DS.textMuted;

// ── Fiori-style nav icons ────────────────────────────────────────────────────
const ICONS = {
  home: (active, c) => active
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill={c}><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,

  tasks: (active, c) => active
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill={c}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>,

  apps: (active, c) => active
    ? <svg width="22" height="22" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1.5" fill={c}/><rect x="14" y="3" width="7" height="7" rx="1.5" fill={c}/><rect x="3" y="14" width="7" height="7" rx="1.5" fill={c}/><rect x="14" y="14" width="7" height="7" rx="1.5" fill={c}/></svg>
    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>,

  bell: (active, c) => active
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill={c}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0" stroke={c} fill="none" strokeWidth="1.8" strokeLinecap="round"/></svg>
    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,

  arey: (active, c) => active
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill={c}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,

  me: (active, c) => active
    ? <svg width="22" height="22" viewBox="0 0 24 24" fill={c}><circle cx="12" cy="7" r="4.5"/><path d="M3.5 21.5c0-4.142 3.806-7.5 8.5-7.5s8.5 3.358 8.5 7.5" stroke={c} fill="none" strokeWidth="1.8" strokeLinecap="round"/></svg>
    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
};

// ── Module tile icons ────────────────────────────────────────────────────────
const MOD_ICON = {
  '/':             c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  '/arey':         c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  '/aira':         c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  '/tasks':        c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  '/reminders':    c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  '/attendance':   c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  '/calendar':     c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  '/email':        c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,6 12,13 2,6"/></svg>,
  '/courier':      c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  '/brands':       c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  '/skus':         c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>,
  '/clients':      c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>,
  '/vendors':      c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>,
  '/sales':        c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  '/sales-orders': c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>,
  '/crm/leads':    c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  '/quotations':   c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  '/samples':      c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v6l5 8a2 2 0 01-1.7 3H5.7A2 2 0 014 17l5-8V3"/></svg>,
  '/purchase-orders': c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>,
  '/formulations': c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v6l5 8a2 2 0 01-1.7 3H5.7A2 2 0 014 17l5-8V3"/><line x1="9" y1="3" x2="15" y2="3"/></svg>,
  '/rawmaterials': c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
  '/production':   c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20V8l-6 4V8l-6 4V4H2z"/></svg>,
  '/warehouse':    c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 8.35V20a2 2 0 01-2 2H4a2 2 0 01-2-2V8.35A2 2 0 013.26 6.5l8-3.2a2 2 0 011.48 0l8 3.2A2 2 0 0122 8.35z"/></svg>,
  '/dispatch':     c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  '/finance':      c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  '/hr':           c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  '/radar':        c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="12" r="6" strokeOpacity="0.6"/><circle cx="12" cy="12" r="10" strokeOpacity="0.3"/></svg>,
  '/rm-inwards':   c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 8.35V20a2 2 0 01-2 2H4a2 2 0 01-2-2V8.35A2 2 0 013.26 6.5l8-3.2a2 2 0 011.48 0l8 3.2A2 2 0 0122 8.35z"/></svg>,
  '/client360':    c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/><line x1="10.88" y1="21.94" x2="15.46" y2="14"/></svg>,
  '/360':          c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/><line x1="10.88" y1="21.94" x2="15.46" y2="14"/></svg>,
};

const DEFAULT_ICON = c => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;

// ── Module group color palette ────────────────────────────────────────────────
const GROUP_COLOR = {
  '':                              { bg: '#F4E6D2', ink: '#7A4A1C' },
  'Core / Workspace':              { bg: '#F4E6D2', ink: '#7A4A1C' },
  'Master Data':                   { bg: '#FAF1D9', ink: '#876328' },
  'Sales & Distribution':          { bg: '#F0EBFA', ink: '#5C4290' },
  'CRM & Customer Service':        { bg: '#FFF1E0', ink: '#8C5610' },
  'E-Commerce & Channels':         { bg: '#EBF3ED', ink: '#3E6E48' },
  'B2B / Trade Portal':            { bg: '#E2F4F6', ink: '#1C6B72' },
  'Marketing & Creative':          { bg: '#FCE7EF', ink: '#9B2C5A' },
  'R&D / PLM':                     { bg: '#F4ECFB', ink: '#5A4290' },
  'Artwork & Labels':              { bg: '#FCE7F1', ink: '#9B2C66' },
  'Regulatory & EHS':              { bg: '#F0E9FA', ink: '#5C3E8E' },
  'Quality Management':            { bg: '#F4ECFB', ink: '#5A4290' },
  'Manufacturing':                 { bg: '#EBF3ED', ink: '#3E6E48' },
  'Sourcing & Procurement':        { bg: '#FFE5EA', ink: '#8E3354' },
  'Warehouse & Inventory':         { bg: '#FFE5EA', ink: '#8E3354' },
  'Logistics & Dispatch':          { bg: '#FFE5EA', ink: '#8E3354' },
  'Asset & Maintenance':           { bg: '#F2F2EE', ink: '#3A3A3C' },
  'Finance & Controlling':         { bg: '#E6F4EC', ink: '#1F6B47' },
  'Human Resources':               { bg: '#FFE5EA', ink: '#8E3354' },
  'Analytics & Intelligence':      { bg: '#EAF1FB', ink: '#2E5A8E' },
  'Investor Readiness':            { bg: '#F4ECFB', ink: '#5A4290' },
  'Reseller Portal':               { bg: '#E2F4F6', ink: '#1C6B72' },
  'Governance, Risk & Compliance': { bg: '#F2F2EE', ink: '#3A3A3C' },
  'System Administration':         { bg: '#F4E6D2', ink: '#7A4A1C' },
};

const MODULE_GROUPS = NAV_CATALOG;

// Me — the records that belong to the person rather than the business.
const ME_ITEMS = [
  { label: 'Attendance',   path: '/attendance',       group: 'Human Resources' },
  { label: 'Leave',        path: '/leave',            group: 'Human Resources' },
  { label: 'Salary',       path: '/salary',           group: 'Human Resources' },
  { label: 'My Documents', path: '/pulse-documents',  group: 'Human Resources' },
  { label: 'Handbook',     path: '/handbook',         group: 'Core / Workspace' },
];


// ── App Finder list primitives ───────────────────────────────────────────────
// Module names run long ("Packaging Materials", "Formulation Control Centre").
// A tile grid has to clamp them; a full-width row does not, which is why this
// is a grouped list rather than a launcher grid.
const LIST_CARD = {
  background: DS.surface,
  border: `1px solid ${DS.border}`,
  borderRadius: DS.r.lg,
  overflow: 'hidden',
  marginBottom: 18,
};

// Press feedback belongs in CSS, not React state: :active fires on touch-down
// with no re-render, which is what makes it feel immediate rather than laggy.
const APP_FINDER_CSS = `
.aone-appfinder-row {
  transition: background-color 120ms ease, transform 120ms ease;
}
.aone-appfinder-row:active {
  background-color: rgba(0,0,0,0.045);
  transform: scale(0.995);
}
@media (prefers-reduced-motion: reduce) {
  .aone-appfinder-row { transition: none; }
  .aone-appfinder-row:active { transform: none; }
}
`;

const SectionHeader = ({ children }) => (
  <div style={{
    // Sticky so you always know which section you are scrolling through —
    // with 158 modules the header is the only orientation cue.
    position: 'sticky', top: -12, zIndex: 1,
    background: DS.bg,
    padding: '10px 4px 6px',
    fontSize: DS.type.xs, fontWeight: 700,
    color: DS.textMuted,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    fontFamily: DS.font,
  }}>
    {children}
  </div>
);

const ModuleRow = ({ item, group, last, active, onGo }) => {
  const clr = GROUP_COLOR[group || item.group] || GROUP_COLOR[''];
  const iconFn = MOD_ICON[item.path] || NAV_ICONS[ICON_MAP[item.path]] || DEFAULT_ICON;
  return (
    <div
      className="aone-appfinder-row"
      onClick={() => onGo(item.path)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        // 56px keeps the touch target well above the 44pt minimum even with
        // a two-line label.
        minHeight: 56, padding: '9px 14px',
        cursor: 'pointer',
        background: active ? `${ACCENT}0E` : 'transparent',
        // Inset divider, aligned to the label rather than the icon.
        borderBottom: last ? 'none' : `1px solid ${DS.divider}`,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 9,
        background: active ? ACCENT : clr.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {iconFn(active ? '#fff' : clr.ink)}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: DS.type.md,
          fontWeight: active ? 700 : 500,
          color: active ? ACCENT : DS.text,
          lineHeight: 1.3,
          fontFamily: DS.font,
          overflowWrap: 'anywhere',
        }}>
          {item.label}
        </div>
      </div>

      {item.isNew && (
        <span style={{
          background: DS.dangerBg, color: DS.danger,
          fontSize: DS.type.xxs, fontWeight: 700,
          padding: '2px 6px', borderRadius: 4, flexShrink: 0,
        }}>NEW</span>
      )}

      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={DS.borderStrong}
           strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    </div>
  );
};

export default function MobileBottomNav({ T, unreadCount, areyUnread = 0, onNotifToggle, onProfileClick, profilePhoto, onScanSearch, openTrigger }) {
  const [sheetOpen, setSheetOpen] = useState(false);

  // External open request — the header's waffle icon and an edge-swipe both
  // route through this counter instead of owning sheet state themselves.
  useEffect(() => {
    if (openTrigger) setSheetOpen(true);
  }, [openTrigger]);
  const [meOpen, setMeOpen] = useState(false);
  // Day status for the Me sheet's identity plate — fetched when the sheet
  // opens, best-effort (the sheet renders fine without it).
  const [meDay, setMeDay] = useState(null);
  useEffect(() => {
    if (!meOpen) return;
    api.get('/attendance/today').then(({ data }) => setMeDay(data || null)).catch(() => setMeDay(null));
  }, [meOpen]);
  const [searchQuery, setSearchQuery] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  // Whether the server search came back with nothing. Kept here because the
  // "nothing matches" state belongs to the sheet as a whole: modules alone
  // being empty is not a dead end when records or actions matched.
  const [serverEmpty, setServerEmpty] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canAccess, user, logout } = useAuth();
  const isFounder = user?.role === 'founder';
  const isSecurityGuard = user?.role === 'security_guard';
  const searchRef = useRef(null);
  const sheetRef = useRef(null);

  // Sliding active-pill behind the tab bar — springs to the active tab.
  const tabRefs = useRef({});
  const [pill, setPill] = useState({ left: 0, width: 0, visible: false });

  const dragStartY = useRef(null);
  const handleSheetTouchStart = useCallback(e => {
    dragStartY.current = e.touches[0].clientY;
  }, []);
  const handleSheetTouchEnd = useCallback(e => {
    if (dragStartY.current === null) return;
    const dy = e.changedTouches[0].clientY - dragStartY.current;
    if (dy > 80) setSheetOpen(false);
    dragStartY.current = null;
  }, []);

  useEffect(() => {
    if (sheetOpen) setTimeout(() => searchRef.current?.focus(), 350);
    else setSearchQuery('');
  }, [sheetOpen]);

  const tabs = isSecurityGuard
    ? [
        { key: 'home',  label: 'Home',  path: '/' },
        { key: 'apps',  label: 'Search' },
        { key: 'me',    label: 'Me' },
      ]
    : [
        // Arey lives on the orb — one Arey entry, not two.
        { key: 'home',  label: 'Home',  path: '/' },
        { key: 'tasks', label: 'Tasks', path: '/tasks' },
        { key: 'apps',  label: 'Modules' },
        { key: 'me',    label: 'Me' },
      ];

  const activeKey = sheetOpen
    ? 'apps'
    : meOpen
    ? 'me'
    : pathname === '/'
    ? 'home'
    : pathname === '/tasks'
    ? 'tasks'
    : pathname.startsWith('/arey')
    ? 'arey'
    : pathname === '/directory' || pathname === '/launchpad'
    ? 'apps'
    : null;

  // Position the sliding pill: a fixed-width (46px) highlight centred on the
  // active tab. Hidden when no tab is active (e.g. on a deep route).
  useLayoutEffect(() => {
    const el = activeKey && tabRefs.current[activeKey];
    if (!el) { setPill(p => ({ ...p, visible: false })); return; }
    const W = 46;
    setPill({ left: el.offsetLeft + el.offsetWidth / 2 - W / 2, width: W, visible: true });
  }, [activeKey]);

  // Re-centre the pill on viewport changes (rotation, keyboard, resize).
  useEffect(() => {
    const onResize = () => {
      const el = activeKey && tabRefs.current[activeKey];
      if (!el) return;
      const W = 46;
      setPill({ left: el.offsetLeft + el.offsetWidth / 2 - W / 2, width: W, visible: true });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [activeKey]);

  const haptic = () => {
    try { window.Capacitor?.Plugins?.Haptics?.impact({ style: 'light' }); } catch (_) {}
  };

  const handleTab = (tab) => {
    haptic();
    // Modules station → the Directory page (docs/design/mobile/03). The old
    // App Finder sheet stays for the security-guard 'Search' tab only.
    if (tab.key === 'apps' && !isSecurityGuard) { setMeOpen(false); setSheetOpen(false); navigate('/directory'); }
    else if (tab.key === 'apps') { setMeOpen(false); setSheetOpen(p => !p); }
    else if (tab.key === 'bell')   { setSheetOpen(false); setMeOpen(false); onNotifToggle(); }
    else if (tab.key === 'me')     { setSheetOpen(false); setMeOpen(p => !p); }
    else { setSheetOpen(false); setMeOpen(false); navigate(tab.path); }
  };

  // Recently opened modules. With 158 of them, the handful a person actually
  // uses every day is the difference between this sheet being a launcher and
  // being a directory you have to search every time.
  const RECENTS_KEY = 'aone.recentModules';
  const [recents, setRecents] = useState(() => {
    try { return (() => { try { const v = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } })(); } catch (_) { return []; }
  });

  const go = (path) => {
    haptic();
    setSheetOpen(false);
    setRecents(prev => {
      const next = [path, ...prev.filter(p => p !== path)].slice(0, 6);
      try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
    navigate(path);
  };

  // Access rules, independent of the search box — recents need the same gate.
  const permitted = (item) => {
    if (item.founderOnly && !isFounder) return false;
    // Hub entries (e.g. /360) — visible when the user holds ANY listed module.
    if (item.anyOf ? !(isFounder || item.anyOf.some(p => canAccess(p))) : !canAccess(item.path)) return false;
    return true;
  };

  const q = searchQuery.trim().toLowerCase();
  const filteredGroups = MODULE_GROUPS.map(g => ({
    ...g,
    items: g.items.filter(item => {
      if (!permitted(item)) return false;
      if (!q) return true;
      // Match the section name too: someone hunting "quality" should find the
      // modules filed under Quality Management even when the word is not in
      // the module's own label.
      return (item.label || '').toLowerCase().includes(q)
          || (g.group || '').toLowerCase().includes(q);
    }),
  })).filter(g => g.items.length > 0);

  const resultCount = filteredGroups.reduce((n, g) => n + g.items.length, 0);

  const recentItems = q ? [] : recents
    .map(path => {
      for (const g of MODULE_GROUPS) {
        const hit = g.items.find(i => i.path === path);
        if (hit) return permitted(hit) ? { ...hit, group: g.group } : null;
      }
      return null;
    })
    .filter(Boolean)
    .filter(i => i.path !== pathname);

  return (
    <>
      <style>{APP_FINDER_CSS}</style>

      {/* A scanned code is a query you didn't have to type, so it feeds the
          same search as the keyboard. This used to close the sheet and jump
          straight to /skus, which meant scanning a raw material, a carton or an
          AWB landed on the SKU page and found nothing. */}
      {scanOpen && (
        <BarcodeScanner
          title="Scan to search"
          subtitle="Scan any barcode — white-on-black labels supported"
          onDetected={(text) => { setScanOpen(false); setSearchQuery(text); }}
          onClose={() => setScanOpen(false)}
        />
      )}
      {/* ── App Finder Sheet ─────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 955,
        pointerEvents: sheetOpen ? 'auto' : 'none',
      }}>
        {/* Backdrop */}
        <div
          onClick={() => setSheetOpen(false)}
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.40)',
            opacity: sheetOpen ? 1 : 0,
            transition: 'opacity 0.22s ease',
          }}
        />

        {/* Sheet panel — SAP Fiori App Finder style */}
        <div
          ref={sheetRef}
          onTouchStart={handleSheetTouchStart}
          onTouchEnd={handleSheetTouchEnd}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: '91%',
            background: DS.bg,
            borderRadius: `${DS.r.xl}px ${DS.r.xl}px 0 0`,
            display: 'flex', flexDirection: 'column',
            transform: sheetOpen ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
            overflow: 'hidden',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.16)',
          }}
        >
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, flexShrink: 0 }}>
            <div style={{ width: 32, height: 4, background: DS.borderStrong, borderRadius: 2 }} />
          </div>

          {/* Sheet header */}
          <div style={{
            padding: '8px 16px 0',
            flexShrink: 0,
            background: DS.bg,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: DS.type.lg, fontWeight: 700, color: DS.text, letterSpacing: '-0.01em', fontFamily: DS.fontDisplay, lineHeight: 1.2 }}>
                  Search
                </div>
                <div style={{ fontSize: DS.type.xs, color: DS.textMuted, marginTop: 2, fontFamily: DS.font }}>
                  Orders, people, stock — or browse all modules
                </div>
              </div>
              <button
                onClick={() => setSheetOpen(false)}
                style={{
                  width: 32, height: 32, borderRadius: DS.r.md,
                  background: DS.surfaceAlt,
                  border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DS.textSec} strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Search bar */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              // "The Field, option B" — Didot hairline: no box, a single ink
              // baseline with a serif italic placeholder (Atelier Fitting XIII).
              background: 'transparent',
              border: 'none',
              borderBottom: `1.5px solid ${DS.text}`,
              borderRadius: 0, padding: '0 2px 10px',
              height: 44,
              marginBottom: 8,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DS.textMuted} strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={searchRef}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search anything…"
                data-no-uppercase
                style={{
                  flex: 1, border: 'none', outline: 'none',
                  background: 'transparent',
                  fontSize: DS.type.lg, color: DS.text,
                  fontFamily: DS.fontDisplay, fontStyle: 'italic',
                  padding: 0,
                }}
              />
              {searchQuery.length > 0 && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    display: 'flex', alignItems: 'center', cursor: 'pointer',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DS.textMuted} strokeWidth="2.2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
              <button
                onClick={() => setScanOpen(true)}
                aria-label="Scan barcode to search"
                style={{ background: 'none', border: 'none', padding: 0, marginLeft: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: DS.type.md, lineHeight: 1 }}
              >
                📷
              </button>
            </div>
          </div>

          {/* ── Divider ── */}
          <div style={{ height: 1, background: DS.divider, margin: '8px 0 0', flexShrink: 0 }} />

          {/* Scrollable module grid — 3-column tile layout */}
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '12px 16px',
            paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
            WebkitOverflowScrolling: 'touch',
          }}>
            {/* Recently opened — the handful of modules a person actually
                lives in, so the common case is one tap and no scrolling. */}
            {recentItems.length > 0 && (
              <>
                <SectionHeader>Recent</SectionHeader>
                <div style={LIST_CARD}>
                  {recentItems.map((item, i) => (
                    <ModuleRow
                      key={'r-' + item.path}
                      item={item}
                      last={i === recentItems.length - 1}
                      active={pathname === item.path}
                      onGo={go}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Records and actions from the server, above the module list. What
                someone wants is nearly always a thing — a consignment, a person,
                a batch — and only occasionally the module that contains it, so
                modules rank last rather than first. */}
            <GlobalSearchResults
              query={searchQuery}
              onNavigate={() => setSheetOpen(false)}
              onEmptyChange={setServerEmpty}
            />

            {q && resultCount > 0 && (
              <div style={{
                fontSize: DS.type.sm, color: DS.textMuted, fontFamily: DS.font,
                padding: '2px 4px 10px',
              }}>
                {resultCount} {resultCount === 1 ? 'app' : 'apps'}
              </div>
            )}

            {/* Only a true dead end — no records, no actions, no modules. */}
            {resultCount === 0 && serverEmpty && (
              <div style={{
                textAlign: 'center', padding: '48px 24px', color: DS.textMuted,
                fontFamily: DS.font,
              }}>
                <div style={{ fontSize: DS.type.xl, fontFamily: DS.fontDisplay, color: DS.textSec, marginBottom: 6 }}>
                  Nothing matches “{searchQuery}”
                </div>
                <div style={{ fontSize: DS.type.sm }}>
                  Try an ID, a product name, or a person.
                </div>
              </div>
            )}

            {filteredGroups.map(group => (
              <div key={group.group}>
                {group.group && <SectionHeader>{group.group}</SectionHeader>}
                <div style={LIST_CARD}>
                  {group.items.map((item, i) => (
                    <ModuleRow
                      key={item.path}
                      item={item}
                      group={group.group}
                      last={i === group.items.length - 1}
                      active={pathname === item.path}
                      onGo={go}
                    />
                  ))}
                </div>
              </div>
            ))}


          </div>
        </div>
      </div>

      {/* ── Me ────────────────────────────────────────────────────────────────
          Everything about the person using the app: who they are, and the
          five records that belong to them rather than to the business. The Me
          tab used to open the change-photo modal, which meant attendance,
          leave, salary and documents were only reachable by hunting through
          158 modules. */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 970,
          pointerEvents: meOpen ? 'auto' : 'none',
        }}
      >
        <div
          onClick={() => setMeOpen(false)}
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.40)',
            opacity: meOpen ? 1 : 0,
            transition: 'opacity 0.22s ease',
          }}
        />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          maxHeight: '88%',
          background: DS.bg,
          borderRadius: `${DS.r.xl}px ${DS.r.xl}px 0 0`,
          display: 'flex', flexDirection: 'column',
          transform: meOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          overflow: 'hidden',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.16)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, flexShrink: 0 }}>
            <div style={{ width: 32, height: 4, background: DS.borderStrong, borderRadius: 2 }} />
          </div>

          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '8px 16px calc(24px + env(safe-area-inset-bottom, 0px))',
            WebkitOverflowScrolling: 'touch',
          }}>
            {/* Identity plate — Maison treatment to match the v2 home/Index.
                Tapping the photo keeps the existing change-photo flow. */}
            {(() => {
              const att = meDay?.attendance || meDay || {};
              const clockedIn = !!att.clockIn && !att.clockOut;
              const dayDone = !!att.clockOut;
              const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }); } catch { return ''; } };
              return (
                <div style={{
                  background: 'linear-gradient(160deg, #340C12 0%, #5E1A24 70%)', color: '#F6EFE7',
                  borderRadius: 20, padding: '18px 18px 16px', marginBottom: 16,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div
                      onClick={() => { setMeOpen(false); onProfileClick && onProfileClick(); }}
                      style={{
                        width: 58, height: 58, borderRadius: '50%',
                        background: profilePhoto ? `center/cover url(${profilePhoto})` : 'rgba(246,239,231,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#F6EFE7', fontSize: DS.type.xl, fontWeight: 700,
                        fontFamily: DS.fontDisplay, flexShrink: 0, cursor: 'pointer',
                        border: '1.5px solid rgba(201,160,99,0.55)',
                      }}
                    >
                      {!profilePhoto && (user?.name || '?').trim().charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: DS.type.xxl, fontFamily: DS.fontDisplay, lineHeight: 1.15, letterSpacing: '-0.01em',
                      }}>
                        {user?.name || 'Your account'}
                      </div>
                      <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#C9A063', marginTop: 4 }}>
                        {(user?.role || '').replace(/_/g, ' ')}{user?.department ? ` · ${user.department}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.14)' }}>
                    <span style={{ width: 6, height: 6, borderRadius: 99, background: dayDone ? 'rgba(246,239,231,0.5)' : clockedIn ? '#7FC08F' : '#C9A063', flexShrink: 0 }} />
                    <span style={{ fontSize: 11.5, opacity: 0.85 }}>
                      {dayDone ? `Day closed · ${timeOf(att.clockIn)} — ${timeOf(att.clockOut)}`
                        : clockedIn ? `On the floor since ${timeOf(att.clockIn)}`
                        : meDay === null ? 'Attendance' : 'Not clocked in yet'}
                    </span>
                    <span onClick={() => { setMeOpen(false); go('/attendance'); }}
                      style={{ marginLeft: 'auto', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#C9A063', cursor: 'pointer' }}>
                      Attendance →
                    </span>
                  </div>
                </div>
              );
            })()}

            <SectionHeader>Your record</SectionHeader>
            <div style={LIST_CARD}>
              {ME_ITEMS.map((item, i) => (
                <ModuleRow
                  key={item.path}
                  item={item}
                  group={item.group}
                  last={i === ME_ITEMS.length - 1}
                  active={pathname === item.path}
                  onGo={(path) => { setMeOpen(false); go(path); }}
                />
              ))}
            </div>

            {/* Update the app.
                A phone can sit on a cached bundle after a deploy — the version
                banner catches most of it, but if you have missed it there was
                no way to force the issue from inside the app, only a URL you
                had to remember. This does the same work in place and keeps
                you signed in. */}
            <div
              onClick={async () => {
                try {
                  if ('serviceWorker' in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map(r => r.unregister()));
                  }
                  if ('caches' in window) {
                    const keys = await caches.keys();
                    await Promise.all(keys.map(k => caches.delete(k)));
                  }
                } catch { /* best effort — reload regardless */ }
                window.location.replace(`/?fresh=${Date.now()}`);
              }}
              style={{
                padding: '14px 16px', marginBottom: 10,
                borderRadius: DS.r.lg, textAlign: 'left',
                background: DS.surface, border: `1px solid ${DS.border}`,
                cursor: 'pointer', fontSize: DS.type.md, fontWeight: 600,
                color: DS.text, fontFamily: DS.font,
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              Update the app
            </div>

            <div
              onClick={() => { logout(); navigate('/login'); }}
              style={{
                padding: '14px 16px',
                borderRadius: DS.r.lg, textAlign: 'left',
                background: DS.surface,
                border: `1px solid ${DS.dangerBorder || DS.border}`,
                cursor: 'pointer', fontSize: DS.type.md, fontWeight: 600,
                color: DS.danger, fontFamily: DS.font,
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Sign out
            </div>
          </div>
        </div>
      </div>

      {/* ── The Capsule ─────────────────────────────────────────────────────────
          Floating night-lacquer bar + Aira orb (docs/design/mobile/02-capsule-bar
          .html, option B). Same tabs/sheets/haptics as before — only the bar's
          body changed. The old sliding-pill state is kept but unrendered so the
          measurement effects stay harmless. */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 960,
        display: 'flex', alignItems: 'center', gap: 10,
        paddingRight: 'max(10px, env(safe-area-inset-right, 0px))',
        pointerEvents: 'none',
      }}>
        <div style={{
          flex: 1, pointerEvents: 'auto',
          // Docked glass bar — flush with the bottom so it never floats over
          // page buttons; content padding accounts for it exactly.
          background: `${DS.surface}F2`,
          WebkitBackdropFilter: 'blur(28px) saturate(1.6)', backdropFilter: 'blur(28px) saturate(1.6)',
          borderTop: `1px solid ${DS.border}`, borderRadius: `${DS.r.xl}px ${DS.r.xl}px 0 0`,
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          padding: '10px 12px calc(10px + env(safe-area-inset-bottom, 0px))',
          boxShadow: DS.shadow.panel,
        }}>
          {tabs.map(tab => {
            const isActive = activeKey === tab.key;
            const color = isActive ? ACCENT : MUTED;
            const iconFn = ICONS[tab.key] || ICONS.apps;
            return (
              <div
                key={tab.key}
                ref={(el) => { tabRefs.current[tab.key] = el; }}
                className="aone-mtab"
                onClick={() => handleTab(tab)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  width: 52, cursor: 'pointer', position: 'relative',
                  WebkitTapHighlightColor: 'transparent', color,
                }}
              >
                <div className="aone-mtab-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {iconFn(isActive, color)}
                  {tab.key === 'arey' && areyUnread > 0 && (
                    <div style={{
                      position: 'absolute', top: -4, right: -9,
                      minWidth: 15, height: 15, borderRadius: 8, padding: '0 4px',
                      background: DS.danger, color: DS.textInv,
                      fontSize: DS.type.xxs, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{areyUnread > 99 ? '99+' : areyUnread}</div>
                  )}
                </div>
                <span style={{
                  fontSize: DS.type.xxs, letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontWeight: isActive ? 700 : 500, fontFamily: DS.font, lineHeight: 1,
                }}>
                  {tab.label}
                </span>
                {isActive && (
                  <div style={{ position: 'absolute', bottom: -7, width: 4, height: 4, borderRadius: 99, background: ACCENT }} />
                )}
              </div>
            );
          })}
        </div>
        {/* The orb — Arey, always reachable by the thumb, from any screen. */}
        <div
          onClick={() => { haptic(); setSheetOpen(false); setMeOpen(false); navigate('/arey'); }}
          className="aone-mtab"
          style={{
            pointerEvents: 'auto', flexShrink: 0, position: 'relative',
            width: 46, height: 46, borderRadius: 999, alignSelf: 'center',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `radial-gradient(circle at 32% 28%, #8A3341, ${DS.accentHover} 74%)`,
            border: `1px solid ${DS.accentHover}`,
            boxShadow: '0 10px 24px rgba(110,31,43,0.30), inset 0 1.5px 1px rgba(255,255,255,0.24)',
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          }}
        >
          <span style={{ fontFamily: DS.fontDisplay, color: DS.textInv, fontSize: DS.type.xxxl }}>A</span>
          {areyUnread > 0 && (
            <div style={{
              position: 'absolute', top: -3, right: -3,
              minWidth: 17, height: 17, borderRadius: 9, padding: '0 4px',
              background: DS.danger, color: DS.textInv,
              fontSize: DS.type.xxs, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1.5px solid ${DS.surface}`,
            }}>{areyUnread > 99 ? '99+' : areyUnread}</div>
          )}
        </div>
      </div>

      <style>{`
        .aone-mtab { transition: transform 0.12s ease; }
        .aone-mtab:active { transform: scale(0.9); }
        .aone-mtile { transition: transform 0.12s ease; }
        .aone-mtile:active { transform: scale(0.95); }
        @media (prefers-reduced-motion: reduce) {
          .aone-mtab-pill { transition: opacity 0.2s ease !important; }
          .aone-mtab-icon { transition: none !important; transform: none !important; }
          .aone-mtab, .aone-mtile { transition: none !important; }
          .aone-mtab:active, .aone-mtile:active { transform: none !important; }
        }
      `}</style>
    </>
  );
}
